/**
 * Every failure code the tellering module emits, and what a person does about it.
 *
 * `src/lib/error-messages.ts` is the application-wide table and it carries three teller codes. This
 * one is the module's own, and it is exhaustive on purpose: the codes below were read out of
 * `Microcredit.Tellering.Contracts.TellerErrors` and out of the FluentValidation validators beside
 * each feature, which are the other half of the surface and the half that is easy to forget. A
 * screen that renders only the sixteen best-known codes leaves an operator reading
 * "teller.note_too_long" with no idea that the note is capped at 500 characters.
 *
 * The text here is a *remedy*, shown under the server's own message rather than instead of it — the
 * server says what happened, this says what to do next.
 */
const REMEDIES: Record<string, string> = {
  // ── vault and till
  'teller.vault_not_found': 'Check the branch: a vault is created once per branch and this one has none.',
  'teller.vault_not_active': 'That vault is suspended or closed. A manager reactivates it before cash moves.',
  'teller.vault_insufficient_cash':
    'The vault does not hold that much. Draw cash from the bank into the vault first.',
  'teller.vault_already_exists': 'That branch already has a vault of that name. Use the existing one.',
  'teller.till_not_found': 'That till does not exist, or it belongs to a branch you cannot see.',
  'teller.till_number_taken': 'Another till at this branch already carries that number. Choose another.',
  'teller.till_not_active': 'That drawer is suspended or closed. It cannot take a session or cash.',
  'teller.till_insufficient_cash': 'The drawer does not hold that much cash. Count it before banking.',
  'teller.till_cash_limit_exceeded':
    'This would take the drawer over its holding limit. Bank cash to the vault first — the limit is on the drawer, not on the transaction.',
  'teller.till_not_field': 'Only a field till is assigned to an officer. A counter till is opened as a session.',
  'teller.till_not_empty':
    'This drawer still holds cash and is currently held by another officer. Bank the cash before it changes hands.',
  'teller.branch_mismatch': 'The vault and the till belong to different branches, or the branch is out of your reach.',

  // ── sessions
  'teller.session_not_found': 'That shift does not exist, or it belongs to a branch you cannot see.',
  'teller.session_already_open':
    'A session is already open on this drawer, or you already hold one open elsewhere. Close that one first.',
  'teller.session_not_open': 'Open your till before posting cash transactions.',
  'teller.session_pending_close':
    'This shift has been submitted for countersignature. Record a fresh count to supersede the declaration.',
  'teller.session_not_yours': 'A drawer belongs to one person, and this one is not yours.',
  'teller.no_open_till':
    'You have no open till. Open a session on a counter till, or ask to be assigned a field till for today.',
  'teller.field_till_needs_no_session':
    'A field till is made live by assigning it to an officer for the day. It never takes a session, and it is never counted or declared here.',

  // ── counting and closing
  'teller.declaration_disagrees_with_count':
    'The declared figure is not what the closing count adds up to. Recount, or correct the declaration to match the sheet.',
  'teller.denomination_invalid': 'That is not a Ghana cedi note or coin. Use the denominations on the sheet.',
  'teller.denomination_count_negative': 'A denomination count cannot be negative. Leave it blank for none.',
  'teller.count_empty': 'A count needs at least one denomination line, even if the drawer is empty.',
  'teller.movement_amount_not_positive': 'A cash movement must be greater than zero.',
  'teller.currency_mismatch': 'The amount is in a different currency from the drawer. This branch transacts in GHS.',
  'teller.movement_kind_invalid':
    'Only vault movements are posted here. Customer cash and over-short adjustments are written by the deposit, withdrawal, or close that causes them.',

  // ── validation, which is the other half of the surface
  'teller.payload_required': 'The request body was empty. Fill the form in and submit it again.',
  'teller.till_id_required': 'This movement names a till. Choose the drawer the cash is going to or coming from.',
  'teller.session_id_required': 'The shift was not identified. Reopen it from the session list.',
  'teller.officer_required': 'Choose the loan officer who will carry the drawer.',
  'teller.officer_not_found': 'No such user. Choose an officer from the list rather than typing an identifier.',
  'teller.assignment_date_required': 'An assignment is good for one day, and that day has to be named.',
  'teller.assignment_date_in_past': 'A field till cannot be assigned for a day that has passed. Use today or later.',
  'teller.reason_required': 'A reason is required, and it is capped at 500 characters.',
  'teller.branch_required': 'The branch was not identified. Choose one in the header and try again.',
  'teller.till_number_required': 'A till number is required, up to 16 characters.',
  'teller.till_name_required': 'A till name is required, up to 120 characters.',
  'teller.vault_name_required': 'A vault name is required, up to 120 characters.',
  'teller.till_type_invalid': 'Choose either a counter till or a field till.',
  'teller.cash_limit_invalid': 'A holding limit must be greater than zero. Leave it empty for no limit.',
  'teller.declared_cash_negative': 'A declaration cannot be negative. Zero is legitimate — an empty drawer.',
  'teller.note_too_long': 'The note is capped at 500 characters.',
  'teller.count_purpose_invalid': 'Choose an opening, spot, or closing count.',
  'teller.page_invalid': 'That page does not exist. Go back to the first page.',
  'teller.page_size_invalid': 'A page holds between 1 and 200 rows.',

  // ── the neighbouring modules a counter posts through
  'savings.account_wrong_class':
    'A security deposit account does not take an ordinary deposit. Use the security deposit rail instead.',
  'savings.account_dormant':
    'This account is dormant, so a withdrawal is refused. Reactivation needs savings.dormancy.manage — a supervisor.',
  'savings.account_not_active': 'This account is frozen or closed. Nothing posts to it.',
  'savings.insufficient_available_balance':
    'The available balance does not cover the amount plus the product’s withdrawal fee. The fee is not visible to a teller — ask a supervisor for the shortfall.',
  // The two are different refusals from different checks: one is "there is not enough", the other
  // is "there is enough, but the product will not let the balance fall that far".
  'savings.below_minimum_balance':
    'This would take the balance under the product’s minimum. The minimum is not visible to a teller — pay out less, or ask a supervisor for the figure.',
  'savings.account_not_withdrawable':
    'This account cannot be withdrawn from. A security deposit is released by an offset or at closure, never over a counter.',
  'savings.product_class_not_withdrawable':
    'This product class is not withdrawable at all. Nothing about this account will make it payable over a counter.',
  'savings.account_not_found': 'No such savings account, or it belongs to a branch you cannot see.',
  'savings.amount_not_positive': 'The amount must be greater than zero.',
  'savings.currency_mismatch': 'The amount is in a different currency from the account. This branch transacts in GHS.',
  'savings.value_date_out_of_window':
    'That value date is outside the window the ledger accepts. Post it in the branch’s current business day.',
  'loans.repayment_exceeds_outstanding':
    'The amount is more than the round owes. Collect the exact figure the server names; the excess belongs in savings.',
  'loans.cycle_not_active': 'That round is not active, so no collection can be posted against it.',
  'loans.repayment_not_positive': 'A collection must be greater than zero.',
  'loans.repayment_before_disbursement':
    'The value date is before the round was disbursed. Nothing can be collected against money that had not been paid out yet.',
  'loans.cycle_not_found': 'That round does not exist, or it belongs to a branch you cannot see.',

  // ── the drawer's own permission refusal, which is not a teller code
  'auth.permission_denied':
    'Your role does not hold the permission this drawer requires. Counter cash needs teller.deposit.post or teller.withdrawal.post.',
}

/** The remedy for a code, or null where the server's own message is the whole of it. */
export const tellerErrorRemedy = (code: string | undefined): string | null =>
  code ? (REMEDIES[code] ?? null) : null
